import type { Meal, MealWeek, PlannedMeal } from './types'

export const MEAL_WEEKS = ['This week', 'Next week', 'Next next week'] as const
export const MEAL_FIELDS = ['name', 'ingredients', 'recipeUrl', 'labelIds'] as const
export const PLANNED_MEAL_FIELDS = ['week', 'sortOrder', 'shopped', 'completedAt'] as const
export type RemainingMealsChoice = 'keep' | 'complete'
export type PlannedMealPatch = Partial<Pick<PlannedMeal, 'week' | 'sortOrder' | 'shopped' | 'completedAt'>>

export function sameMealData(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false
  const left = a as Record<string, unknown>
  const right = b as Record<string, unknown>
  const keys = Object.keys(left)
  return keys.length === Object.keys(right).length && keys.every((key) => Object.hasOwn(right, key) && sameMealData(left[key], right[key]))
}

// Each persisted plan entry represents one addition to a weekly list. Keep
// completed entries in this history; moves, undo and replication retries reuse
// the same entry ID and must not inflate the count.
export function countMealUses(entries: readonly Pick<PlannedMeal, 'id' | 'mealId'>[]): ReadonlyMap<string, number> {
  const counts = new Map<string, number>()
  const seen = new Set<string>()
  for (const entry of entries) {
    if (seen.has(entry.id)) continue
    seen.add(entry.id)
    counts.set(entry.mealId, (counts.get(entry.mealId) ?? 0) + 1)
  }
  return counts
}

export function compareMealsByUsage(a: Pick<Meal, 'id' | 'name'>, b: Pick<Meal, 'id' | 'name'>, counts: ReadonlyMap<string, number>) {
  return (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0)
    || a.name.localeCompare(b.name)
    || a.id.localeCompare(b.id)
}

export function mealsForWeek(entries: PlannedMeal[], week: MealWeek) {
  return entries.filter((entry) => entry.week === week && !entry.completedAt)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id))
}

export function nextMealOrder(entries: PlannedMeal[], week: MealWeek) {
  return Math.max(0, ...entries.filter((entry) => entry.week === week && !entry.completedAt).map((entry) => entry.sortOrder)) + 1
}

// Use neighbor IDs rather than an index when saving: another device may have
// inserted or completed a meal while this user was dragging.
export function planMealReorder(entries: PlannedMeal[], movedId: string, week: MealWeek, beforeId: string | null, afterId: string | null): Array<{ id: string; patch: PlannedMealPatch }> {
  const moved = entries.find((entry) => entry.id === movedId && !entry.completedAt)
  if (!moved || !MEAL_WEEKS[week] || movedId === beforeId || movedId === afterId) return []
  const destination = mealsForWeek(entries, week).filter((entry) => entry.id !== movedId)
  const before = destination.find((entry) => entry.id === beforeId)
  const after = destination.find((entry) => entry.id === afterId)
  // If one neighbor disappeared, keep the surviving anchor. If both vanished,
  // append rather than overwriting a collaborator's changes.
  const index = after ? destination.indexOf(after) : before ? destination.indexOf(before) + 1 : destination.length
  destination.splice(index, 0, moved)
  if (moved.week === week && mealsForWeek(entries, week).every((entry, i) => entry.id === destination[i]?.id)) return []

  const previous = destination[index - 1]?.sortOrder
  const next = destination[index + 1]?.sortOrder
  const rank = previous === undefined ? (next ?? 2) - 1 : next === undefined ? previous + 1 : (previous + next) / 2
  const weekPatch = moved.week === week ? {} : { week }
  if (Number.isFinite(rank) && (previous === undefined || rank > previous) && (next === undefined || rank < next)) {
    return [{ id: movedId, patch: { ...weekPatch, sortOrder: rank } }]
  }
  // Tied ranks (e.g. concurrent inserts), or exhausted floating-point space.
  return destination.flatMap((entry, i) => {
    const patch: PlannedMealPatch = entry.id === movedId ? { ...weekPatch } : {}
    if (entry.sortOrder !== i + 1) patch.sortOrder = i + 1
    return Object.keys(patch).length ? [{ id: entry.id, patch }] : []
  })
}

export function bumpMeal(entry: PlannedMeal, entries: PlannedMeal[], direction: 1 | -1 = 1): Partial<PlannedMeal> | null {
  const destination = entry.week + direction
  if (destination < 0 || destination > 2 || entry.completedAt) return null
  const week = destination as MealWeek
  return { week, sortOrder: nextMealOrder(entries, week), shopped: false }
}

export function advanceMealWeeks(entries: PlannedMeal[], remaining: RemainingMealsChoice, completedAt: string): Array<{ id: string; patch: PlannedMealPatch }> {
  const changes: Array<{ id: string; patch: PlannedMealPatch }> = []
  if (remaining === 'complete') {
    for (const entry of mealsForWeek(entries, 0)) changes.push({ id: entry.id, patch: { completedAt } })
  }
  let order = remaining === 'keep' ? nextMealOrder(entries, 0) : 1
  for (const entry of mealsForWeek(entries, 1)) changes.push({ id: entry.id, patch: { week: 0, sortOrder: order++ } })
  order = 1
  for (const entry of mealsForWeek(entries, 2)) changes.push({ id: entry.id, patch: { week: 1, sortOrder: order++ } })
  return changes
}

// Used by the server and browser so independent offline edits merge consistently.
export function mergeMealDocument<T extends Meal | PlannedMeal>(master: T, incoming: T): T {
  const merged = { ...master, fieldClock: { ...master.fieldClock } }
  const fields = 'mealId' in master ? PLANNED_MEAL_FIELDS : MEAL_FIELDS
  for (const field of fields) {
    if ((incoming.fieldClock[field] ?? '') > (master.fieldClock[field] ?? '')) {
      Object.assign(merged, { [field]: (incoming as unknown as Record<string, unknown>)[field] })
      merged.fieldClock[field] = incoming.fieldClock[field]
    }
  }
  merged.updatedAt = master.updatedAt > incoming.updatedAt ? master.updatedAt : incoming.updatedAt
  return merged
}
